import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

const money = (v) => `₹${Number(v || 0).toFixed(2)}`;
const safe = (v, fb = "-") => (v === null || v === undefined || v === "" ? fb : v);

const pickList = (data, keys = []) => {
  if (Array.isArray(data)) return data;
  for (const k of keys) if (Array.isArray(data?.[k])) return data[k];
  return [];
};

export default function AdminOfflineBilling() {
  const navigate = useNavigate();

  // ---------------- AUTH ----------------
  useEffect(() => {
    const token = localStorage.getItem("token");
    const u = JSON.parse(localStorage.getItem("user") || "null");
    const role = String(u?.role || "").toUpperCase();
    if (!token || !(role.includes("ADMIN") || role.includes("STAFF"))) navigate("/login", { replace: true });
  }, [navigate]);

  // ---------------- USERS ----------------
  const [userQ, setUserQ] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  // ---------------- ADDRESSES (OPTIONAL) ----------------
  const [addrLoading, setAddrLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [addressId, setAddressId] = useState("");

  // ---------------- PRODUCTS ----------------
  const [prodQ, setProdQ] = useState("");
  const [productsLoading, setProductsLoading] = useState(false);
  const [products, setProducts] = useState([]);

  // ---------------- CART ----------------
  const [cart, setCart] = useState([]);

  // ---------------- PAYMENT ----------------
  const [paymentMethod, setPaymentMethod] = useState("OFFLINE");
  const [paymentRef, setPaymentRef] = useState("");
  const [markDelivered, setMarkDelivered] = useState(false);

  // ---------------- ORDERS LIST (GET) ----------------
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [recentOrders, setRecentOrders] = useState([]);

  // ---------------- UI ----------------
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // ---------------- DEBOUNCE REFS ----------------
  const userTimer = useRef(null);
  const prodTimer = useRef(null);

  // ---------------- FETCH USERS (auto) ----------------
  const fetchUsers = async (q) => {
    setUsersLoading(true);
    try {
      const res = await axiosInstance.get("/api/users", { 
        params: q ? { search: q, role: "USER" } : { role: "USER" } 
      });
      setUsers(pickList(res?.data, ["users", "data"]));
    } catch {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  // ---------------- FETCH PRODUCTS (auto) ----------------
  const fetchProducts = async (q) => {
    setProductsLoading(true);
    try {
      // ✅ If backend supports search param, this works. If not, it still returns all.
      const res = await axiosInstance.get("/api/products", { params: q ? { search: q } : {} });
      setProducts(pickList(res?.data, ["products", "data"]));
    } catch {
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  // ✅ OPTIONAL Addresses: safe + fallback
  const fetchAddressesForUser = async (uid) => {
    setAddrLoading(true);
    setAddresses([]);
    setAddressId("");
    try {
      const res1 = await axiosInstance.get("/api/addresses/admin/all", { params: { userId: uid } });
      let list = pickList(res1?.data, ["addresses", "data"]);

      if (!list.length) {
        const res2 = await axiosInstance.get("/api/addresses/admin/all");
        const all = pickList(res2?.data, ["addresses", "data"]);
        list = all.filter((a) => {
          const aUserId = a?.userId ?? a?.UserId ?? a?.user_id ?? a?.user?.id;
          return Number(aUserId) === Number(uid);
        });
      }

      setAddresses(list);
      if (list.length) setAddressId(String(list[0].id));
    } catch {
      setAddresses([]);
      setAddressId("");
    } finally {
      setAddrLoading(false);
    }
  };

  // ---------------- FETCH RECENT OFFLINE ORDERS ----------------
  const fetchRecentOfflineOrders = async () => {
    setOrdersLoading(true);
    try {
      const res = await axiosInstance.get("/api/orders/admin");
      const list = pickList(res?.data, ["orders", "data"]);

      const offline = list.filter((o) =>
        ["OFFLINE", "CASH", "BANK", "UPI"].includes(String(o?.paymentMethod || "").toUpperCase())
      );

      offline.sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));
      setRecentOrders(offline.slice(0, 20));
    } catch {
      setRecentOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  // ✅ initial loads (products should show automatically)
  useEffect(() => {
    fetchProducts(""); // load ALL products initially (auto visible)
    fetchRecentOfflineOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ auto search users while typing (debounced)
  useEffect(() => {
    if (selectedUser) return; // if user already selected, don’t keep searching
    if (userTimer.current) clearTimeout(userTimer.current);
    userTimer.current = setTimeout(() => {
      const q = userQ.trim();
      if (q.length >= 1) fetchUsers(q);
      else setUsers([]);
    }, 350);
    return () => {
      if (userTimer.current) clearTimeout(userTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userQ, selectedUser]);

  // ✅ auto search products while typing (debounced) BUT also keep list visible
  useEffect(() => {
    if (prodTimer.current) clearTimeout(prodTimer.current);
    prodTimer.current = setTimeout(() => {
      const q = prodQ.trim();
      // If backend supports server-side search: fetchProducts(q)
      // If not, we still already loaded all products; filtering is done below.
      // To support both, call API only if q length >= 2:
      if (q.length >= 2) fetchProducts(q);
      if (q.length === 0) fetchProducts(""); // back to all products
    }, 350);
    return () => {
      if (prodTimer.current) clearTimeout(prodTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prodQ]);

  // load addresses when user selected
  useEffect(() => {
    if (selectedUser?.id) fetchAddressesForUser(selectedUser.id);
    else {
      setAddresses([]);
      setAddressId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser?.id]);

  // ---------------- CART HELPERS ----------------
  const addProduct = (p) => {
    setCart((prev) => {
      const idx = prev.findIndex((x) => x.product?.id === p.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: Number(copy[idx].qty || 0) + 1 };
        return copy;
      }
      return [...prev, { product: p, qty: 1 }];
    });
  };

  const updateQty = (productId, qty) => {
    const q = Math.max(1, Number(qty || 1));
    setCart((prev) => prev.map((x) => (x.product?.id === productId ? { ...x, qty: q } : x)));
  };

  const removeItem = (productId) => setCart((prev) => prev.filter((x) => x.product?.id !== productId));

  const clearAll = () => {
    setCart([]);
    setPaymentMethod("OFFLINE");
    setPaymentRef("");
    setMarkDelivered(false);
    setMsg("");
    setErr("");
    setAddresses([]);
    setAddressId("");
  };

  // totals (UI only)
  const totals = useMemo(() => {
    const subtotal = cart.reduce((sum, x) => sum + Number(x.product?.price || 0) * Number(x.qty || 0), 0);
    const mrpTotal = cart.reduce((sum, x) => sum + Number(x.product?.mrp || 0) * Number(x.qty || 0), 0);
    return { subtotal, mrpTotal, saved: Math.max(0, mrpTotal - subtotal) };
  }, [cart]);

  // ✅ product list filter (client-side) — products always visible
  const filteredProducts = useMemo(() => {
    const q = prodQ.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      `${p?.name || ""} ${p?.brand || ""} ${p?.sku || ""}`.toLowerCase().includes(q)
    );
  }, [products, prodQ]);

  // ---------------- CREATE OFFLINE ORDER (POST) ----------------
  const createOfflineOrder = async () => {
    setMsg("");
    setErr("");

    if (!selectedUser?.id) return setErr("Please select a user");
    if (!cart.length) return setErr("Please add at least 1 product");

    const payload = {
      userId: Number(selectedUser.id),
      items: cart.map((x) => ({ productId: Number(x.product.id), qty: Number(x.qty) })),
      paymentMethod: String(paymentMethod || "OFFLINE").toUpperCase(),
      paymentRef: paymentRef?.trim() || undefined,
      markDelivered: !!markDelivered,
    };

    // ✅ address optional
    const addrNum = Number(addressId);
    if (addressId && Number.isFinite(addrNum) && addrNum > 0) payload.addressId = addrNum;

    setCreating(true);
    try {
      const res = await axiosInstance.post("/api/orders/admin/offline", payload);
      setMsg(res?.data?.msg || "Offline order created successfully");

      clearAll();
      setSelectedUser(null);
      setUsers([]);
      setUserQ("");

      fetchRecentOfflineOrders();
      fetchProducts(""); // refresh products list / stock changes
    } catch (e) {
      setErr(e?.response?.data?.msg || "Failed to create offline order");
    } finally {
      setCreating(false);
    }
  };

  const canCreate = !!selectedUser?.id && cart.length > 0 && !creating;

  return (
    <div style={s.page}>
      <style>{`
        @media (max-width: 980px){
          .ob-grid{ grid-template-columns: 1fr !important; }
          .ob-prodgrid{ grid-template-columns: 1fr 1fr !important; }
          .ob-formgrid{ grid-template-columns: 1fr !important; }
        }
        @media (max-width: 520px){
          .ob-prodgrid{ grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={s.header}>
        <div>
          <div style={s.h1}>Offline Billing</div>
          <div style={s.sub}>Create offline order (OFFLINE / CASH / BANK / UPI)</div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button style={s.btn} onClick={() => navigate("/admin/dashboard")}>← Dashboard</button>
          <button style={s.btn} onClick={() => navigate("/admin/orders")}>View Orders</button>
          <button style={s.btn} onClick={() => navigate("/admin/staff-orders")}>Staff Orders</button>
        </div>
      </div>

      {(err || msg) && (
        <div style={{ ...s.alert, ...(err ? s.alertErr : s.alertOk) }}>
          {err || msg}
        </div>
      )}

      <div style={s.grid} className="ob-grid">
        {/* LEFT */}
        <div style={s.card}>
          <div style={s.cardTitle}>1) Select User</div>

          <input
            style={s.input}
            placeholder="Type user name / phone / userID / email (auto search)"
            value={userQ}
            onChange={(e) => setUserQ(e.target.value)}
          />
          <div style={s.help}>
            Auto search starts while typing. Select a user from the list.
          </div>

          {selectedUser ? (
            <div style={s.selectedBox}>
              <div style={{ fontWeight: 950, fontSize: 14, color: "#111" }}>
                {selectedUser.name}{" "}
                <span style={{ opacity: 0.7, fontWeight: 850 }}>
                  ({selectedUser.userID || selectedUser.id})
                </span>
              </div>
              <div style={s.muted}>
                {safe(selectedUser.phone)} • {safe(selectedUser.email)}
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={s.label}>Address (Optional)</div>

                {addrLoading ? (
                  <div style={s.muted2}>Loading addresses...</div>
                ) : addresses.length > 0 ? (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <select
                      style={{ ...s.input, flex: 1 }}
                      value={addressId}
                      onChange={(e) => setAddressId(e.target.value)}
                    >
                      <option value="">No Address (Optional)</option>
                      {addresses.map((a) => (
                        <option key={a.id} value={a.id}>
                          {safe(a.name)} • {safe(a.city)} • {safe(a.pincode)}
                        </option>
                      ))}
                    </select>
                    <button style={s.btn} onClick={() => fetchAddressesForUser(selectedUser.id)}>
                      Refresh
                    </button>
                  </div>
                ) : (
                  <>
                    <div style={{ ...s.muted2, marginTop: 6 }}>
                      No address found. You can still create order without address.
                    </div>
                    <input
                      style={{ ...s.input, marginTop: 10 }}
                      placeholder="Enter addressId (optional)"
                      value={addressId}
                      onChange={(e) => setAddressId(e.target.value)}
                    />
                  </>
                )}

                <div style={s.help}>
                  If address selected, delivery charge slab may apply (backend).
                </div>
              </div>

              <button style={s.smallBtn} onClick={() => setSelectedUser(null)}>
                Change User
              </button>
            </div>
          ) : (
            <div style={{ marginTop: 10 }}>
              {usersLoading ? (
                <div style={s.muted2}>Searching users...</div>
              ) : users.length > 0 ? (
                <div style={s.list}>
                  {users.map((u) => (
                    <button key={u.id} style={s.listItemBtn} onClick={() => setSelectedUser(u)}>
                      <div style={{ fontWeight: 950, color: "#111" }}>
                        {u.name} <span style={{ opacity: 0.65 }}>({u.userID || u.id})</span>
                      </div>
                      <div style={s.muted}>{safe(u.phone)} • {safe(u.email)}</div>
                    </button>
                  ))}
                </div>
              ) : (
                <div style={s.muted2}>Start typing to search users...</div>
              )}
            </div>
          )}

          <hr style={s.hr} />

          {/* PRODUCTS */}
          <div style={s.cardTitle}>2) Add Products</div>

          <input
            style={s.input}
            placeholder="Type product name / brand / sku (products auto visible)"
            value={prodQ}
            onChange={(e) => setProdQ(e.target.value)}
          />

          <div style={{ marginTop: 10 }}>
            {productsLoading && products.length === 0 ? (
              <div style={s.muted2}>Loading products...</div>
            ) : (
              <div style={s.prodGrid} className="ob-prodgrid">
                {(filteredProducts || []).slice(0, 60).map((p) => (
                  <div key={p.id} style={s.prodCard}>
                    <div style={{ fontWeight: 950, color: "#111" }}>{p.name}</div>
                    <div style={s.muted}>
                      {p.brand ? `${p.brand} • ` : ""}
                      {p.sku ? `SKU: ${p.sku}` : ""}
                    </div>
                    <div style={{ marginTop: 8, fontWeight: 950, color: "#111" }}>
                      {money(p.price)}{" "}
                      {p.mrp ? <span style={s.strike}>{money(p.mrp)}</span> : null}
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, color: "#666", fontWeight: 800 }}>
                      Stock: {safe(p.stockQty, 0)}
                    </div>
                    <button style={s.primaryBtn} onClick={() => addProduct(p)}>
                      + Add
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!productsLoading && filteredProducts.length === 0 ? (
              <div style={s.muted2}>No products found for your search.</div>
            ) : null}
          </div>
        </div>

        {/* RIGHT */}
        <div style={s.card}>
          <div style={s.cardTitle}>3) Bill Items</div>

          {cart.length === 0 ? (
            <div style={s.muted2}>No items added</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Product</th>
                    <th style={s.th}>Price</th>
                    <th style={s.th}>Qty</th>
                    <th style={s.th}>Total</th>
                    <th style={s.th}>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {cart.map((x) => (
                    <tr key={x.product.id}>
                      <td style={s.td}>
                        <div style={{ fontWeight: 950, color: "#111" }}>{x.product.name}</div>
                        <div style={s.muted}>{x.product.sku ? `SKU: ${x.product.sku}` : ""}</div>
                      </td>
                      <td style={s.td}>{money(x.product.price)}</td>
                      <td style={s.td}>
                        <input
                          style={s.qty}
                          type="number"
                          min={1}
                          value={x.qty}
                          onChange={(e) => updateQty(x.product.id, e.target.value)}
                        />
                      </td>
                      <td style={s.td}>{money(Number(x.product.price || 0) * Number(x.qty || 0))}</td>
                      <td style={s.td}>
                        <button style={s.dangerBtn} onClick={() => removeItem(x.product.id)}>
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={s.summary}>
                <div style={s.sumRow}><span>MRP Total</span><b>{money(totals.mrpTotal)}</b></div>
                <div style={s.sumRow}><span>Subtotal</span><b>{money(totals.subtotal)}</b></div>
                <div style={s.sumRow}><span>You Save</span><b>{money(totals.saved)}</b></div>
              </div>
            </div>
          )}

          <hr style={s.hr} />

          <div style={s.cardTitle}>4) Payment & Delivery</div>

          <div style={s.formGrid} className="ob-formgrid">
            <div>
              <div style={s.label}>Payment Method</div>
              <select style={s.input} value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                <option value="OFFLINE">OFFLINE</option>
                <option value="CASH">CASH</option>
                <option value="BANK">BANK</option>
                <option value="UPI">UPI</option>
              </select>
            </div>

            <div>
              <div style={s.label}>Payment Ref (optional)</div>
              <input
                style={s.input}
                placeholder="UTR / UPI ref / note"
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
              <input type="checkbox" checked={markDelivered} onChange={(e) => setMarkDelivered(e.target.checked)} />
              <div style={{ fontWeight: 950, color: "#111" }}>Mark Delivered</div>
              <span style={{ fontSize: 12, color: "#666", fontWeight: 800 }}>
                (Triggers spend/unlock/referral release)
              </span>
            </div>

            <div style={{ marginTop: 8 }}>
              <div style={s.label}>Address Selected</div>
              <div style={{ fontWeight: 900, color: "#111" }}>
                {addressId ? `AddressId: ${addressId}` : "No address (optional)"}
              </div>
            </div>
          </div>

          <div style={s.footerActions}>
            <button style={s.btn} onClick={clearAll} disabled={creating}>Clear</button>
            <button
              style={{
                ...s.primaryBig,
                opacity: canCreate ? 1 : 0.6,
                cursor: canCreate ? "pointer" : "not-allowed",
              }}
              onClick={createOfflineOrder}
              disabled={!canCreate}
            >
              {creating ? "Creating..." : `Create Order • ${money(totals.subtotal)}`}
            </button>
          </div>

          <hr style={s.hr} />

          {/* Recent Offline Orders */}
          <div style={s.cardTitle}>Recent Offline Orders</div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            <button style={s.btn} onClick={fetchRecentOfflineOrders} disabled={ordersLoading}>
              {ordersLoading ? "Loading..." : "Refresh Orders"}
            </button>
          </div>

          {ordersLoading ? (
            <div style={s.muted2}>Loading recent offline orders...</div>
          ) : recentOrders.length === 0 ? (
            <div style={s.muted2}>No offline orders found.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Order</th>
                    <th style={s.th}>User</th>
                    <th style={s.th}>Amount</th>
                    <th style={s.th}>Pay</th>
                    <th style={s.th}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((o) => (
                    <tr key={o.id}>
                      <td style={s.td}>#{o.id}</td>
                      <td style={s.td}>{o?.user?.name || o?.User?.name || safe(o.userId)}</td>
                      <td style={s.td}>{money(o.totalAmount || o.total || o.grandTotal)}</td>
                      <td style={s.td}>
                        {safe(o.paymentMethod)}
                        {o.CreatedByAdmin && (
                          <div style={{ marginTop: '4px', fontSize: '11px', color: '#666', fontWeight: 800 }}>
                            By: {o.CreatedByAdmin.name}
                          </div>
                        )}
                      </td>
                      <td style={s.td}>{o.createdAt ? new Date(o.createdAt).toLocaleString() : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- LIGHT THEME (white bg, black text) ---------- */
const s = {
  page: {
    padding: 18,
    minHeight: "100vh",
    background: "#f4f6f8",
    color: "#111",
    fontFamily: "Segoe UI, Inter, system-ui, -apple-system, Arial",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  h1: { fontSize: 22, fontWeight: 950, color: "#111" },
  sub: { fontSize: 13, color: "#666", marginTop: 4 },

  grid: { display: "grid", gridTemplateColumns: "1.15fr 0.85fr", gap: 12 },

  card: {
    background: "#fff",
    borderRadius: 14,
    padding: 14,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    border: "1px solid rgba(0,0,0,0.06)",
  },

  cardTitle: { fontSize: 14, fontWeight: 950, color: "#111", marginBottom: 10 },

  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #ddd",
    outline: "none",
    fontWeight: 700,
    background: "#fff",
    color: "#111",
  },

  btn: {
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #ddd",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 900,
    whiteSpace: "nowrap",
    color: "#111",
  },

  primaryBtn: {
    marginTop: 10,
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #111",
    background: "#111",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 950,
  },

  primaryBig: {
    padding: "12px 14px",
    borderRadius: 12,
    border: "1px solid #111",
    background: "linear-gradient(135deg,#111,#2b2b2b)",
    color: "#fff",
    fontWeight: 950,
    minWidth: 220,
  },

  dangerBtn: {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: "1px solid #ffd2d2",
    background: "#fff0f0",
    cursor: "pointer",
    fontWeight: 950,
    color: "#b00020",
  },

  smallBtn: {
    marginTop: 10,
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid #ddd",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 900,
    color: "#111",
  },

  list: { marginTop: 10, display: "grid", gap: 8 },

  listItemBtn: {
    textAlign: "left",
    padding: 10,
    borderRadius: 12,
    border: "1px solid #eee",
    background: "#fafafa",
    cursor: "pointer",
  },

  selectedBox: {
    marginTop: 10,
    padding: 10,
    borderRadius: 12,
    border: "1px solid #e8e8e8",
    background: "#fafafa",
  },

  prodGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, marginTop: 8 },

  prodCard: {
    border: "1px solid #eee",
    borderRadius: 14,
    padding: 12,
    background: "#fff",
    boxShadow: "0 1px 8px rgba(0,0,0,0.05)",
  },

  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", fontSize: 12, color: "#666", padding: 10, borderBottom: "1px solid #eee" },
  td: { padding: 10, borderBottom: "1px solid #f2f2f2", verticalAlign: "middle", color: "#111" },

  qty: { width: 70, padding: "8px 10px", borderRadius: 10, border: "1px solid #ddd", fontWeight: 900, color: "#111" },

  summary: { marginTop: 10, padding: 12, borderRadius: 12, background: "#fafafa", border: "1px solid #eee" },
  sumRow: { display: "flex", justifyContent: "space-between", padding: "6px 0", color: "#111" },

  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },

  label: { fontSize: 12, color: "#666", fontWeight: 900, marginBottom: 6 },
  help: { fontSize: 12, color: "#777", marginTop: 6, fontWeight: 700 },

  footerActions: { display: "flex", justifyContent: "space-between", gap: 10, marginTop: 14, flexWrap: "wrap" },

  alert: { marginBottom: 12, padding: 12, borderRadius: 12, fontWeight: 900, border: "1px solid" },
  alertOk: { background: "#ecfff1", borderColor: "#b9f6c7", color: "#0f7a2f" },
  alertErr: { background: "#fff0f0", borderColor: "#ffd2d2", color: "#b00020" },

  muted: { fontSize: 12, color: "rgba(0,0,0,.65)", marginTop: 4, fontWeight: 700 },
  muted2: { fontSize: 13, color: "#666", marginTop: 10, fontWeight: 700 },

  hr: { border: "none", borderTop: "1px solid #eee", margin: "14px 0" },
  strike: { marginLeft: 8, textDecoration: "line-through", color: "#888", fontWeight: 800 },
};
